import React, { useRef, useEffect, useCallback, useMemo } from "react";
import styled from "@emotion/styled";

import RibbonCard from "./RibbonCard";
import { safeGet } from "src/shared/utils";
import { ISinglePin } from "src/shared/types";
import colors from "src/styles/variables.scss";

const RibbonSliderContainer = styled.section<{ height: number }>`
  padding: 0;
  z-index: 10;
  background: ${colors.blackBackground};
  height: ${({ height }) => height + "px"};
  position: fixed;
  width: inherit;
  overflow-x: hidden;
`;

/**
 *  Refer width from RibbonCard/index.module.scss
 */
const ITEM_WIDTH = 65; /** Item width in vw */

let globalID;

function loop(fn) {
  (function animCards() {
    if (fn()) {
      globalID = requestAnimationFrame(animCards);
    }
  })();
}

interface IRibbonProps {
  pins: {
    hasMore: boolean;
    pageNo: number;
    pageSize: number;
    data: ISinglePin[];
  };
  height: number;
}
const RibbonSlider = (props: IRibbonProps) => {
  const slider = useRef<HTMLDivElement>(null);
  useEffect(() => {
    cancelAnimationFrame(globalID);
    const sliderChildren = safeGet(slider, "current.children", []);
    const containerWidth = slider && slider.current ? slider.current.getBoundingClientRect().width : window.innerWidth;
    const itemWidth = containerWidth * 0.65;
    let firstLeft = (sliderChildren.length * ITEM_WIDTH) / 100;
    loop(function () {
      firstLeft = firstLeft - 1;
      for (let i = 0; i < sliderChildren.length; i++) {
        let leftI = firstLeft + itemWidth * i;
        sliderChildren[i].style.transform = `translateX(${
          (leftI % (sliderChildren.length * itemWidth)) - itemWidth
        }px)`;
      }
      if (firstLeft <= 0) {
        firstLeft = sliderChildren.length * itemWidth;
      }
      return true;
    });
    return () => {
      cancelAnimationFrame(globalID);
    };
  }, [props.pins]);

  const makeRepeated = useCallback(
    (arr, repeats: number): Array<any> => [].concat(...Array.from({ length: repeats }, () => arr)),
    []
  );
  /**
   * repeat elements in case number of pins is less than 3
   * to provide a continuous effect
   */
  const multiplyPins = useCallback(
    (pins: Array<ISinglePin>): Array<ISinglePin> => {
      const numberOfPins = pins.length;
      return numberOfPins === 1 || numberOfPins === 2
        ? makeRepeated(pins, 4 - numberOfPins).map((pin, index) => ({
            ...pin,
            id: `${pin.id}-${index}`,
          }))
        : pins;
    },
    [makeRepeated]
  );

  const pinsArray = useMemo(() => multiplyPins(safeGet(props, "pins.data", [])), [props.pins, multiplyPins]);

  return (
    <RibbonSliderContainer ref={slider} height={props.height}>
      {pinsArray.map((item) => (
        <RibbonCard height={props.height} details={item} key={item.id} />
      ))}
    </RibbonSliderContainer>
  );
};
export default RibbonSlider;
